import { useState } from "react"
import { DailyVideo, useDevices, useLocalSessionId, useParticipantProperty } from "@daily-co/daily-react"
import { ChevronRight, Loader2, Mic, MicOff, Settings, SlidersHorizontal, Video, VideoOff, X } from "lucide-react"
import { Button } from "components/ui/button"
import { AvatarBubble } from "../shared/AvatarBubble"
import { DeviceSettingsMenu } from "./DeviceSettingsMenu"

export type PrejoinChoice = { startAudioOff: boolean; startVideoOff: boolean }

type PrejoinScreenProps = {
  meetingTitle: string
  displayName: string
  joining: boolean
  micOn: boolean
  camOn: boolean
  onToggleMic: () => void
  onToggleCam: () => void
  onJoin: (choice: PrejoinChoice) => void
  onCancel: () => void
  waitingRoomEnabled: boolean
}

/**
 * Device "hair check" shown before joining with the custom renderer. Camera and microphone
 * start off (matching the existing start-with-audio-off / start-with-video-off behaviour);
 * the attendee can preview, pick devices, and recover from permission problems here.
 */
export function PrejoinScreen({
  meetingTitle,
  displayName,
  joining,
  micOn,
  camOn,
  onToggleMic,
  onToggleCam,
  onJoin,
  onCancel,
  waitingRoomEnabled,
}: PrejoinScreenProps) {
  const localSessionId = useLocalSessionId()
  const videoState = useParticipantProperty(localSessionId, "tracks.video.state")
  const { camState, micState } = useDevices()
  const [configurationOpen, setConfigurationOpen] = useState(false)
  const [showDevices, setShowDevices] = useState(false)
  const previewPlayable = camOn && localSessionId && (videoState === "playable" || videoState === "loading" || videoState === "sendable")
  const permissionBlocked = camState === "blocked" || micState === "blocked"

  return (
    <div data-testid="call-prejoin" className="flex h-full w-full flex-col overflow-y-auto px-4 py-4 sm:py-6">
      <div className="my-auto grid w-full max-w-4xl shrink-0 self-center gap-4 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] md:items-center md:gap-6">
        <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-[#1c2530] ring-1 ring-white/10">
          {previewPlayable ? (
            <DailyVideo sessionId={localSessionId} type="video" automirror fit="cover" playsInline className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-3 pb-14 text-white/70">
              <AvatarBubble name={displayName} seed={displayName} className="h-20 w-20 text-2xl" />
              <p className="text-sm">{camOn ? "Starting camera..." : "Camera is off"}</p>
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 flex justify-center gap-2 pb-3" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
            <button
              type="button"
              aria-label={micOn ? "Mute microphone" : "Unmute microphone"}
              aria-pressed={micOn}
              onClick={onToggleMic}
              data-testid="call-prejoin-toggle-mic"
              className={`flex h-11 w-11 items-center justify-center rounded-full text-white shadow-lg ring-1 transition-colors ${micOn ? "bg-black/60 ring-white/40 hover:bg-black/75" : "bg-[#d64545] ring-white/20 hover:bg-[#c03a3a]"}`}
            >
              {micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5" />}
            </button>
            <button
              type="button"
              aria-label={camOn ? "Turn camera off" : "Turn camera on"}
              aria-pressed={camOn}
              onClick={onToggleCam}
              data-testid="call-prejoin-toggle-cam"
              className={`flex h-11 w-11 items-center justify-center rounded-full text-white shadow-lg ring-1 transition-colors ${camOn ? "bg-black/60 ring-white/40 hover:bg-black/75" : "bg-[#d64545] ring-white/20 hover:bg-[#c03a3a]"}`}
            >
              {camOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5" />}
            </button>
            <button
              type="button"
              aria-label="Device settings"
              title={configurationOpen ? "Hide device settings" : "Show device settings"}
              aria-expanded={configurationOpen}
              aria-controls="call-prejoin-configuration"
              onClick={() => setConfigurationOpen((open) => !open)}
              data-testid="call-prejoin-toggle-settings"
              className={`flex h-11 w-11 items-center justify-center rounded-full shadow-lg ring-1 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6a35c] ${configurationOpen ? "bg-[#f6a35c] text-[#151d26] ring-white/40 hover:bg-[#ffb879]" : "bg-black/60 text-white ring-white/40 hover:bg-black/75"}`}
            >
              <Settings className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>
        </div>
        <section id="call-prejoin-configuration" hidden={!configurationOpen} aria-labelledby="call-prejoin-devices-heading" className="min-w-0 space-y-3 rounded-2xl bg-[#151d26] p-4 ring-1 ring-white/10 md:col-span-2 md:row-start-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 id="call-prejoin-devices-heading" className="text-sm font-semibold text-white">Check your devices</h3>
            <button
              type="button"
              onClick={() => setShowDevices(true)}
              aria-haspopup="dialog"
              aria-expanded={showDevices}
              className="flex min-h-11 items-center gap-2 rounded-lg border border-white/20 bg-white/5 px-3 py-2 text-left text-sm font-medium text-white transition-colors hover:bg-white/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6a35c]"
            >
              <SlidersHorizontal className="h-4 w-4 shrink-0 text-[#f6a35c]" aria-hidden="true" />
              <span className="flex-1">Background &amp; audio settings</span>
              <ChevronRight className="h-4 w-4 shrink-0 text-white/60" aria-hidden="true" />
            </button>
          </div>
          <DeviceSettingsMenu inline deviceLayout="row" show={{ camera: true, microphone: true, speaker: true, refresh: true }} />
        </section>
        <div className="flex min-w-0 flex-col justify-center gap-4 rounded-2xl bg-[#151d26] p-4 ring-1 ring-white/10 sm:p-5 md:col-start-2 md:row-start-1">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-white/50">Ready to join</p>
            <h2 className="mt-1 text-lg font-semibold text-white">{meetingTitle}</h2>
            <p className="mt-1 text-sm text-white/70">Joining as {displayName || "Guest"}</p>
            {waitingRoomEnabled && (
              <p className="mt-2 text-xs text-white/60">This meeting has a waiting room. The host will admit you after you join.</p>
            )}
          </div>
          {permissionBlocked && (
            <p role="alert" className="rounded-lg bg-amber-500/15 px-3 py-2 text-xs text-amber-100">
              Your browser is blocking the camera or microphone. Allow access in your browser, then open Device settings to refresh the list. You can still join with them off.
            </p>
          )}
          <button
            type="button"
            onClick={() => setConfigurationOpen((open) => !open)}
            aria-expanded={configurationOpen}
            aria-controls="call-prejoin-configuration"
            className="inline-flex min-h-11 self-start items-center rounded-sm text-sm font-medium text-[#f6a35c] underline underline-offset-4 hover:text-[#ffb879] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#f6a35c]"
          >
            {configurationOpen ? "Hide device settings" : "Configure devices"}
          </button>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              onClick={() => onJoin({ startAudioOff: !micOn, startVideoOff: !camOn })}
              disabled={joining}
              className="flex-1 gap-2"
              data-testid="call-prejoin-join"
            >
              {joining && <Loader2 className="h-4 w-4 animate-spin" />}
              {joining ? "Joining..." : "Join meeting"}
            </Button>
            <Button type="button" variant="secondary" onClick={onCancel} disabled={joining} className="flex-1">
              Cancel
            </Button>
          </div>
        </div>
      </div>
      {showDevices && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/60 p-3 sm:items-center" onClick={() => setShowDevices(false)}>
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Background and audio settings"
            data-testid="call-prejoin-settings"
            onClick={(event) => event.stopPropagation()}
            className="flex max-h-[min(36rem,calc(100%-1.5rem))] w-full max-w-md flex-col rounded-2xl bg-[#0f1419] shadow-2xl ring-1 ring-white/10"
          >
            <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
              <p className="text-sm font-semibold text-white">Background &amp; audio settings</p>
              <button type="button" aria-label="Close background and audio settings" onClick={() => setShowDevices(false)} className="rounded-md p-1.5 text-white/70 hover:bg-white/10 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
              <DeviceSettingsMenu inline showEffects show={{ noise: true }} testId="call-prejoin-extra-settings" />
            </div>
            <div className="border-t border-white/10 px-4 py-3">
              <Button type="button" onClick={() => setShowDevices(false)} className="w-full" data-testid="call-prejoin-settings-done">
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
